import {
  cardTypeSchema,
  catalogCardResponseSchema,
  catalogPrintingResponseSchema,
  catalogSetResponseSchema,
  deckFormatSchema,
  deckLinkSchema,
  deckPlanResponseSchema,
  deckZoneSchema,
  domainSchema,
  formatConfigResponseSchema,
  superTypeSchema,
} from "@openrift/shared/response-schemas";
import { oc } from "@orpc/contract";
import { z } from "zod";

import { deckExportResponseSchema, deckOddsConfigSchema } from "./decks.js";
import { shareOwnerSchema } from "./fields.js";

const encodeDeckCardSchema = z.object({
  cardId: z.string(),
  zone: deckZoneSchema,
  quantity: z.number().int().positive(),
  preferredPrintingId: z.string().nullable(),
  cardName: z.string(),
  cardType: cardTypeSchema,
  superTypes: z.array(superTypeSchema),
  domains: z.array(domainSchema),
});

const encodeDeckInputSchema = z.object({
  format: z.enum(["piltover", "text", "tts"]).optional(),
  cards: z.array(encodeDeckCardSchema),
});

export const publicDeckResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  format: deckFormatSchema,
  formatConfig: formatConfigResponseSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  oddsConfig: deckOddsConfigSchema.nullable(),
  coverCardId: z.string().nullable(),
  coverFaceId: z.string().nullable(),
  coverPosition: z.number().int().nullable(),
  links: z.array(deckLinkSchema),
});

export const publicDeckCardResponseSchema = z.object({
  cardId: z.string(),
  zone: deckZoneSchema,
  quantity: z.number(),
  preferredPrintingId: z.string().nullable(),
  cardName: z.string(),
  cardSlug: z.string(),
  cardType: cardTypeSchema,
  cardTypes: z.array(cardTypeSchema).nonempty(),
  superTypes: z.array(superTypeSchema),
  domains: z.array(domainSchema),
  tags: z.array(z.string()),
  keywords: z.array(z.string()),
  maxCopiesOverride: z.number().nullable(),
  additionalLegendCount: z.number().nullable(),
  banned: z.boolean(),
  energy: z.number().nullable(),
  might: z.number().nullable(),
  power: z.number().nullable(),
  resolvedPrintingId: z.string().nullable(),
  shortCode: z.string().nullable(),
  imageId: z.string().nullable(),
});

export const deckPlanCardMetaResponseSchema = z.object({
  cardId: z.string(),
  cardName: z.string(),
  cardSlug: z.string(),
  cardTypes: z.array(cardTypeSchema).nonempty(),
  imageId: z.string().nullable(),
});

/** Same shapes `/catalog` serves, so a page hands this to the shared catalogue helpers unchanged. */
export const deckCatalogSubsetSchema = z.object({
  sets: z.array(catalogSetResponseSchema),
  cards: z.record(z.string(), catalogCardResponseSchema),
  printings: z.array(catalogPrintingResponseSchema),
});

export const publicDeckDetailResponseSchema = z.object({
  deck: publicDeckResponseSchema,
  cards: z.array(publicDeckCardResponseSchema),
  owner: shareOwnerSchema,
  plan: deckPlanResponseSchema.nullable(),
  planCardMeta: z.array(deckPlanCardMetaResponseSchema),
  customTagAssignments: z.record(z.string(), z.array(z.string())).meta({ examples: [{}] }),
  catalog: deckCatalogSubsetSchema,
});

export const publicDecksContract = {
  share: oc
    .route({ method: "GET", path: "/api/v1/decks/share/{token}", tags: ["Decks"] })
    .meta({ auth: "public", cache: "short" })
    .input(z.object({ token: z.string().min(1) }))
    .errors({ NOT_FOUND: { message: "Not found" } })
    .output(publicDeckDetailResponseSchema),

  // Pure compute over public catalog data, no DB write, so this opens no anonymous write surface.
  encode: oc
    .route({ method: "POST", path: "/api/v1/decks/encode", tags: ["Decks"] })
    .meta({ auth: "public" })
    .input(encodeDeckInputSchema)
    .output(deckExportResponseSchema),
};

export type PublicDecksContract = typeof publicDecksContract;
